"use client";

import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { Button } from "@bcc/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@bcc/components/ui/dialog";
import { useToast } from "@bcc/hooks/use-toast";
import { Service } from "@shared/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Label } from "../ui";
import {
  buildShopDomainSchema,
  flattenZodErrors,
  resolveErrorMessage,
  SHOP_DOMAIN_MODAL_TEST_IDS,
  type ShopDomainErrorMessages,
  type ValidationErrors,
} from "./shop-domain-modal.utils";
import { ShopifyInstallForm, type ShopifyInstallPayload } from "./shopify-install-form";

interface ShopDomainModalProps {
  service?: Service;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: Record<string, string> | ShopifyInstallPayload) => Promise<void>;
  languageOptions?: { label: string; value: string }[];
}

/** Maps a generic (WooCommerce) auth-field name to its modal input test ID. */
const FIELD_INPUT_TEST_IDS: Record<string, string> = {
  shop: SHOP_DOMAIN_MODAL_TEST_IDS.shopInput,
  authKey: SHOP_DOMAIN_MODAL_TEST_IDS.authKeyInput,
  accessToken: SHOP_DOMAIN_MODAL_TEST_IDS.accessTokenInput,
  language: SHOP_DOMAIN_MODAL_TEST_IDS.languageInput,
};

function buildInitialFormData(fields?: { name: string; value?: string }[]): Record<string, string> | undefined {
  if (!fields) return;

  return fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.name] = field.value ?? "";
    return acc;
  }, {});
}

export function ShopDomainModal({ service, isOpen, onClose, onSubmit, languageOptions }: Readonly<ShopDomainModalProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ValidationErrors>({});
  const [formError, setFormError] = useState("");

  // Memoize all translated strings
  const translations = useMemo(
    () => ({
      title: t("onboarding.shopDomainModal.connectPlatform", {
        platform: service?.name,
      }),
      description: t("onboarding.shopDomainModal.description", {
        platform: service?.name,
      }),
      connect: t("onboarding.shopDomainModal.connect"),
      connecting: t("onboarding.shopDomainModal.connecting"),
      cancel: t("onboarding.shopDomainModal.cancel"),
      labels: {
        shop: t("onboarding.shopDomainModal.shopDomain"),
        accessToken: t("onboarding.shopDomainModal.accessToken"),
        authKey: t("onboarding.shopDomainModal.authenticationKey"),
        language: t("onboarding.shopDomainModal.language"),
      },
      errors: {
        enterDomain: t("onboarding.shopDomainModal.errors.enterDomain"),
        enterAccessToken: t("onboarding.shopDomainModal.errors.enterAccessToken"),
        enterAuthKey: t("onboarding.shopDomainModal.errors.enterAuthKey"),
        invalidShopifyDomain: t("onboarding.shopDomainModal.errors.invalidShopifyDomain"),
        invalidWooCommerceDomain: t("onboarding.shopDomainModal.errors.invalidWooCommerceDomain"),
        couldntConnect: t("onboarding.shopDomainModal.errors.couldntConnect"),
      },
      toasts: {
        connectionSuccessful: t("onboarding.shopDomainModal.toasts.connectionSuccessful"),
        storeConnected: t("onboarding.shopDomainModal.toasts.storeConnected", {
          platform: service?.name,
        }),
        connectionFailed: t("onboarding.shopDomainModal.toasts.connectionFailed"),
      },
    }),
    [service?.name, t]
  );

  // Initialize form data when service changes
  const initializeFormData = useCallback(() => {
    const data = buildInitialFormData(service?.authenticationFields);
    if (!data) return;

    setFormData(data);
  }, [service?.authenticationFields]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initialize form fields from service authentication config on mount
    initializeFormData();
  }, [initializeFormData]);

  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    setFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const { [fieldName]: _removed, ...rest } = prev;
      return rest;
    });
    setFormError("");
  }, []);

  const validateAndBuildPayload = useCallback((): {
    payload: Record<string, string>;
    errors: ValidationErrors;
  } => {
    if (!service) {
      return { payload: { ...formData }, errors: {} };
    }

    const messages: ShopDomainErrorMessages = {
      enterDomain: translations.errors.enterDomain,
      enterAccessToken: translations.errors.enterAccessToken,
      enterAuthKey: translations.errors.enterAuthKey,
      invalidShopifyDomain: translations.errors.invalidShopifyDomain,
      invalidWooCommerceDomain: translations.errors.invalidWooCommerceDomain,
    };

    const schema = buildShopDomainSchema(service, messages);
    const result = schema.safeParse(formData);

    if (!result.success) {
      return { payload: { ...formData }, errors: flattenZodErrors(result.error) };
    }

    return { payload: result.data as Record<string, string>, errors: {} };
  }, [formData, service, translations.errors]);

  const handleConnect = useCallback(async () => {
    const { payload, errors } = validateAndBuildPayload();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError("");
      return;
    }

    setFieldErrors({});
    setIsLoading(true);
    setFormError("");

    try {
      await onSubmit(payload);
      toast({
        title: translations.toasts.connectionSuccessful,
        description: translations.toasts.storeConnected,
      });
      onClose();
    } catch (error) {
      console.error("Connection error:", error);
      const errorMessage = resolveErrorMessage(error, translations.errors.couldntConnect);
      setFormError(errorMessage);
      toast({
        title: translations.toasts.connectionFailed,
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [onClose, onSubmit, toast, translations, validateAndBuildPayload]);

  // Memoize field configurations for rendering
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- manual memoization preserved until React Compiler is enabled across the workspace
  const fields = useMemo(() => {
    if (!service?.authenticationFields) return [];

    return service.authenticationFields.map((field) => ({
      ...field,
      fieldId: `field-${field.name}`,
      labelText: translations.labels[field.name as keyof typeof translations.labels] ?? field.label,
    }));
  }, [service?.authenticationFields, translations]);

  const connectButtonText = useMemo(() => {
    if (isLoading) {
      return translations.connecting;
    }
    return translations.connect;
  }, [isLoading, translations]);

  if (!service) return null;

  if (service.id === "shopify") {
    const handleShopifySubmit = async (payload: ShopifyInstallPayload) => {
      setIsLoading(true);
      setFormError("");
      try {
        await onSubmit(payload);
        toast({
          title: translations.toasts.connectionSuccessful,
          description: translations.toasts.storeConnected,
        });
        onClose();
      } catch (err) {
        const errorMessage = resolveErrorMessage(err, translations.errors.couldntConnect);
        setFormError(errorMessage);
        toast({
          title: translations.toasts.connectionFailed,
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-[538px]" data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.container}>
          <DialogHeader>
            <DialogTitle data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.title}>{translations.title}</DialogTitle>
            <DialogDescription data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.description}>{translations.description}</DialogDescription>
          </DialogHeader>
          {formError && (
            <p className="text-sm text-destructive px-1" data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.formError}>
              {formError}
            </p>
          )}
          <ShopifyInstallForm isLoading={isLoading} onSubmit={handleShopifySubmit} onCancel={onClose} languageOptions={languageOptions} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[538px]" data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.container}>
        <DialogHeader>
          <DialogTitle data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.title}>{translations.title}</DialogTitle>
          <DialogDescription data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.description}>{translations.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4" data-tour-id={TOUR_IDS.onboarding.wooCommerceFields}>
          {fields.map((field) => {
            const fieldError = fieldErrors[field.name];
            const errorId = `${field.fieldId}-error`;
            return (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.fieldId}>{field.labelText}</Label>
                <Input
                  id={field.fieldId}
                  name={field.name}
                  data-testid={FIELD_INPUT_TEST_IDS[field.name]}
                  type={field.type === "domain" ? "text" : field.type}
                  placeholder={field.placeholder || ""}
                  value={formData[field.name] || ""}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  disabled={isLoading}
                  aria-invalid={fieldError ? true : undefined}
                  aria-describedby={fieldError ? errorId : undefined}
                />
                {field.helper && !fieldError && <p className="text-sm text-muted-foreground">{field.helper}</p>}
                {fieldError && (
                  <p id={errorId} className="text-sm text-destructive" data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.fieldError}>
                    {fieldError}
                  </p>
                )}
              </div>
            );
          })}
          {formError && (
            <p className="text-sm text-destructive" data-testid={SHOP_DOMAIN_MODAL_TEST_IDS.formError}>
              {formError}
            </p>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading} testId={SHOP_DOMAIN_MODAL_TEST_IDS.cancelButton}>
            {translations.cancel}
          </Button>
          <Button onClick={handleConnect} disabled={isLoading} testId={SHOP_DOMAIN_MODAL_TEST_IDS.connectButton}>
            {connectButtonText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
